/**
 * 목표 관리 페이지 (Phase 1)
 * 오너가 학생의 학습 목표를 설정하고 관리하는 페이지
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../config/api';
import {
  AcademicCapIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
  XMarkIcon,
  ChartBarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { getTodayKST, formatDateKST } from '../../../utils/dateUtils';

interface Student {
  id: string;
  name: string;
  email: string;
}

interface StudyGoal {
  id: string;
  student_id: string;
  organization_id: string;
  goal_setter: 'student' | 'parent';
  goal_period: 'weekly' | 'monthly';
  start_date: string;
  end_date: string;
  daily_study_hours: number | null;
  weekly_attendance_days: number | null;
  target_subjects: Record<string, number> | null;
  notes: string | null;
  is_achieved: boolean;
  achievement_rate: number | null;
  set_by_staff_id: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  remaining_days: number;
  total_days: number;
  progress_percentage: number;
  actual_study_hours?: number;
  actual_attendance_days?: number;
}

interface ActiveGoals {
  student_goals: StudyGoal[];
  parent_goals: StudyGoal[];
}

interface GradeGoal {
  id?: string;
  subject_id: string;
  target_score: number;
  target_grade?: number;
  goal_period: string;
  notes?: string;
}

interface GradeRecord {
  id?: string;
  exam_name: string;
  exam_date: string;
  semester: string;
  subject_id: string;
  score: number;
  grade_level?: number;
  rank_in_class?: number;
  total_students?: number;
  standard_deviation?: number;
  notes?: string;
}

interface Subject {
  id: string;
  organization_id: string;
  name: string;
  is_default: boolean;
  display_order: number;
  is_active: boolean;
}

const GoalManagementPage: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [activeGoals, setActiveGoals] = useState<ActiveGoals | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalStudents, setTotalStudents] = useState(0);

  // 목표 기록 관련 상태
  const [historyGoals, setHistoryGoals] = useState<StudyGoal[]>([]);
  const [filterPeriod, setFilterPeriod] = useState<string>('6months');
  const [filterSetter, setFilterSetter] = useState<string>('all');
  const [filterAchieved, setFilterAchieved] = useState<string>('all');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // 과목 관련 상태
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [isAddingNewSubject, setIsAddingNewSubject] = useState(false);

  // 성적 관련 상태
  const [gradeGoals, setGradeGoals] = useState<GradeGoal[]>([]);
  const [gradeRecords, setGradeRecords] = useState<GradeRecord[]>([]);
  const [newGradeGoal, setNewGradeGoal] = useState<GradeGoal>({
    subject_id: '',
    target_score: 90,
    goal_period: '2025-2학기'
  });
  const [newGradeRecord, setNewGradeRecord] = useState<GradeRecord>({
    exam_name: '중간고사',
    exam_date: getTodayKST(),
    semester: '2025-2학기',
    subject_id: '',
    score: 0
  });

  // 다음 월요일 계산
  const getNextMonday = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
    d.setDate(d.getDate() + diff);
    return formatDateKST(d);
  };

  const getSunday = (mondayStr: string) => {
    const d = new Date(mondayStr);
    d.setDate(d.getDate() + 6);
    return formatDateKST(d);
  };

  // 폼 데이터
  const [formData, setFormData] = useState({
    goal_setter: 'student' as 'student' | 'parent',
    goal_period: 'weekly' as 'weekly' | 'monthly',
    start_date: getNextMonday(),
    end_date: getSunday(getNextMonday()),
    daily_study_hours: 8,
    weekly_attendance_days: 5,
    target_subjects: {} as Record<string, number>,
    notes: '',
    auto_renew: true,
  });

  useEffect(() => {
    if (selectedStudentId) {
      fetchActiveGoals(selectedStudentId);
      // fetchGradeGoals(selectedStudentId); // 성적 기능 준비중 - API 호출 비활성화
      // fetchGradeRecords(selectedStudentId); // 성적 기능 준비중 - API 호출 비활성화
    }
  }, [selectedStudentId]);

  useEffect(() => {
    // 학생이 선택되었을 때 목표 기록 데이터 불러오기
    // 달성 여부는 프론트엔드에서 필터링하므로 의존성에서 제외
    if (selectedStudentId) {
      fetchGoalHistory();
    }
  }, [selectedStudentId, filterPeriod, filterSetter]);

  useEffect(() => {
    // 시작일 변경 시 end_date 자동 설정 (항상 주간: +6일)
    if (formData.start_date) {
      const startDate = new Date(formData.start_date);
      let endDate: Date;

      {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6);
      }

      setFormData((prev) => ({
        ...prev,
        end_date: formatDateKST(endDate),
      }));
    }
  }, [formData.start_date]);

  const searchStudents = useCallback(async (query: string) => {
    try {
      setSearchLoading(true);
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const url = query
        ? `${API_BASE_URL}/v1/owner/members?search=${encodeURIComponent(query)}&limit=20`
        : `${API_BASE_URL}/v1/owner/members?limit=20`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.members || []);
        setTotalStudents(data.total || 0);
      }
    } catch (error) {
      console.error('학생 검색 실패:', error);
    } finally {
      setSearchLoading(false);
      setIsLoading(false);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    searchStudents('');
  }, [searchStudents]);

  // 디바운스 검색
  useEffect(() => {
    const timer = setTimeout(() => {
      searchStudents(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchStudents]);

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/subjects`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSubjects(data);
      } else {
        console.error('과목 목록 조회 실패:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('과목 목록 조회 실패:', error);
    }
  };

  const handleAddNewSubject = async () => {
    if (!newSubjectName.trim()) {
      alert('과목명을 입력해주세요');
      return;
    }

    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/subjects`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newSubjectName,
          display_order: subjects.length + 1,
        }),
      });

      if (response.ok) {
        const newSubject = await response.json();
        setSubjects([...subjects, newSubject]);
        setNewSubjectName('');
        setIsAddingNewSubject(false);
        alert(`과목 "${newSubject.name}"이(가) 추가되었습니다!`);
      } else {
        const error = await response.json();
        alert(`과목 추가 실패: ${error.detail || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('과목 추가 실패:', error);
      alert('과목 추가 중 네트워크 오류가 발생했습니다');
    }
  };

  const fetchGradeGoals = async (studentId: string) => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/grades/goals/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setGradeGoals(data);
      } else {
        console.error('성적 목표 조회 실패:', response.status);
      }
    } catch (error) {
      console.error('성적 목표 조회 실패:', error);
    }
  };

  const fetchGradeRecords = async (studentId: string) => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/grades/records/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setGradeRecords(data);
      } else {
        console.error('성적 기록 조회 실패:', response.status);
      }
    } catch (error) {
      console.error('성적 기록 조회 실패:', error);
    }
  };

  const handleSaveGradeGoals = async () => {
    if (!selectedStudentId) return;

    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

      // 로컬에만 있는 목표들을 서버에 저장
      for (const goal of gradeGoals) {
        if (!goal.id) {
          await fetch(`${API_BASE_URL}/v1/grades/goals/${selectedStudentId}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(goal),
          });
        }
      }

      // 다시 목록 불러오기
      await fetchGradeGoals(selectedStudentId);
      alert('성적 목표가 저장되었습니다!');
    } catch (error) {
      console.error('성적 목표 저장 실패:', error);
      alert('저장 중 오류가 발생했습니다');
    }
  };

  const handleSaveGradeRecords = async () => {
    if (!selectedStudentId) return;

    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

      // 로컬에만 있는 기록들을 서버에 저장
      for (const record of gradeRecords) {
        if (!record.id) {
          await fetch(`${API_BASE_URL}/v1/grades/records/${selectedStudentId}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(record),
          });
        }
      }

      // 다시 목록 불러오기
      await fetchGradeRecords(selectedStudentId);
      alert('성적이 저장되었습니다!');
    } catch (error) {
      console.error('성적 저장 실패:', error);
      alert('저장 중 오류가 발생했습니다');
    }
  };

  const fetchActiveGoals = async (studentId: string) => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

      const response = await fetch(
        `${API_BASE_URL}/v1/study-goals/student/${studentId}/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );


      if (response.ok) {
        const data = await response.json();

        // 오늘 날짜
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // 현재 진행 중이거나 미래 목표만 필터링 (종료일이 오늘 이후)
        const filterCurrentGoals = (goals: StudyGoal[]) => {
          return goals.filter(goal => {
            const endDate = new Date(goal.end_date);
            endDate.setHours(0, 0, 0, 0);
            return endDate >= today;
          });
        };

        // 과거 목표 필터링 (종료일이 오늘 이전)
        const filterPastGoals = (goals: StudyGoal[]) => {
          return goals.filter(goal => {
            const endDate = new Date(goal.end_date);
            endDate.setHours(0, 0, 0, 0);
            return endDate < today;
          });
        };

        // 현재 목표 설정 (진행 중 + 미래)
        const currentGoals = {
          student_goals: filterCurrentGoals(data.student_goals || []),
          parent_goals: filterCurrentGoals(data.parent_goals || [])
        };

        // 과거 목표 설정 (목표 기록용)
        const pastGoals = [
          ...filterPastGoals(data.student_goals || []),
          ...filterPastGoals(data.parent_goals || [])
        ].sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime());


        setActiveGoals(currentGoals);
        setHistoryGoals(pastGoals);
      } else {
        const errorText = await response.text();
        console.error('❌ 활성 목표 조회 실패:', response.status, response.statusText);
        console.error('❌ 에러 내용:', errorText);
      }
    } catch (error) {
      console.error('❌ 활성 목표 조회 실패 (catch):', error);
    }
  };

  const fetchGoalHistory = async () => {
    if (!selectedStudentId) return;

    setIsLoadingHistory(true);
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

      // 필터 파라미터 생성
      const params = new URLSearchParams();

      // 기간 필터 적용 (백엔드는 start_date, end_date를 받음)
      if (filterPeriod !== 'all') {
        const today = new Date();
        let startDate: Date;

        switch (filterPeriod) {
          case '1month':
            startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
            break;
          case '3months':
            startDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
            break;
          case '6months':
            startDate = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
            break;
          case '1year':
            startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
            break;
          default:
            startDate = new Date(0); // 전체
        }

        params.append('start_date', formatDateKST(startDate));
        params.append('end_date', getTodayKST());
      }

      // 설정자 필터 적용
      if (filterSetter !== 'all') {
        params.append('goal_setter', filterSetter);
      }


      const response = await fetch(
        `${API_BASE_URL}/v1/study-goals/student/${selectedStudentId}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // 응답이 배열 형태로 직접 옵니다
        setHistoryGoals(Array.isArray(data) ? data : []);
      } else {
        const errorText = await response.text();
        console.error('❌ 목표 기록 조회 실패:', response.status, response.statusText);
        console.error('❌ 에러 내용:', errorText);
        setHistoryGoals([]);
      }
    } catch (error) {
      console.error('❌ 목표 기록 조회 실패 (catch):', error);
      setHistoryGoals([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStudentId) {
      alert('학생을 선택해주세요');
      return;
    }

    // 프론트엔드 유효성 검사
    if (!formData.start_date) {
      alert('시작 날짜를 입력해주세요');
      return;
    }

    if (!formData.end_date) {
      alert('종료 날짜가 자동으로 설정되지 않았습니다. 목표 기간을 다시 선택해주세요.');
      return;
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);

    if (endDate < startDate) {
      alert(`종료 날짜(${formData.end_date})는 시작 날짜(${formData.start_date})보다 이후여야 합니다`);
      return;
    }

    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/v1/study-goals/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: selectedStudentId,
          goal_setter: formData.goal_setter,
          goal_period: 'weekly',
          start_date: formData.start_date,
          end_date: formData.end_date,
          daily_study_hours: formData.daily_study_hours,
          weekly_attendance_days: formData.weekly_attendance_days,
          target_subjects: null,
          notes: formData.auto_renew
            ? `[자동갱신] ${formData.notes || ''}`
            : formData.notes || null,
        }),
      });

      if (response.ok) {
        const createdGoal = await response.json();
        alert('목표가 성공적으로 생성되었습니다');
        setIsCreateModalOpen(false);
        await fetchActiveGoals(selectedStudentId);
        // 폼 초기화
        setFormData({
          goal_setter: 'student',
          goal_period: 'weekly',
          start_date: getNextMonday(),
          end_date: getSunday(getNextMonday()),
          daily_study_hours: 4,
          weekly_attendance_days: 5,
          target_subjects: {},
          notes: '',
          auto_renew: true,
        });
      } else {
        const error = await response.json();
        console.error('❌ [handleCreateGoal] 서버 에러:', error);
        // 사용자에게 서버에서 온 정확한 에러 메시지 표시
        alert(`목표 생성 실패\n\n${error.detail || '알 수 없는 오류가 발생했습니다'}`);
      }
    } catch (error) {
      console.error('❌ [handleCreateGoal] 요청 실패:', error);
      alert('목표 생성 중 네트워크 오류가 발생했습니다\n\n서버 연결을 확인해주세요');
    }
  };

  const getGoalSetterLabel = (setter: string) => {
    const labels = {
      student: '학생',
      parent: '학부모',
    };
    return labels[setter as keyof typeof labels] || setter;
  };

  const getGoalPeriodLabel = (period: string) => {
    const labels = {
      weekly: '주간',
      monthly: '월간',
    };
    return labels[period as keyof typeof labels] || period;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };


  // renderGoalCards 함수 제거 - 이제 통합된 버튼 사용

  const handleDeleteGoal = async (goalId: string) => {
    if (!window.confirm('이 목표를 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/v1/study-goals/${goalId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        alert('목표가 삭제되었습니다.');
        if (selectedStudentId) fetchActiveGoals(selectedStudentId);
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const renderGoalCard = (goal: StudyGoal, color: string) => {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <span className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-full">
            {getGoalPeriodLabel(goal.goal_period)}
          </span>
          <button
            onClick={() => handleDeleteGoal(goal.id)}
            className="px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
          >
            삭제
          </button>
        </div>

        <div className="space-y-3">
          {/* 기간 */}
          <div className="flex items-center text-sm text-gray-600">
            <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" />
            <span>
              {formatDate(goal.start_date)} ~ {formatDate(goal.end_date)}
            </span>
          </div>

          {/* 일일 목표 시간 */}
          {goal.daily_study_hours && (
            <div className="flex items-center text-sm text-gray-600">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-400" />
              <span>일일 목표: {goal.daily_study_hours}시간</span>
            </div>
          )}

          {/* 주간 출석 목표 */}
          {goal.weekly_attendance_days && (
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircleIcon className="h-5 w-5 mr-2 text-gray-400" />
              <span>주간 출석: {goal.weekly_attendance_days}일</span>
            </div>
          )}

          {/* 과목별 목표 */}
          {goal.target_subjects && Object.keys(goal.target_subjects).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">과목별 목표</p>
              <div className="space-y-1">
                {Object.entries(goal.target_subjects).map(([subject, hours]) => (
                  <div key={subject} className="flex justify-between text-sm text-gray-600">
                    <span>{subject}</span>
                    <span className="font-medium">{hours}시간</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 실제 달성 현황 */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            {/* 실제 공부시간 */}
            {goal.daily_study_hours && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">실제 공부</span>
                <span className="text-sm font-semibold text-gray-900">
                  {goal.actual_study_hours !== undefined ? `${goal.actual_study_hours}시간` : '-'}
                  <span className="text-xs text-gray-400 ml-1">/ 목표 {goal.daily_study_hours * goal.total_days}시간</span>
                </span>
              </div>
            )}

            {/* 실제 출석일 */}
            {goal.weekly_attendance_days && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">실제 출석</span>
                <span className="text-sm font-semibold text-gray-900">
                  {goal.actual_attendance_days !== undefined ? `${goal.actual_attendance_days}일` : '-'}
                  <span className="text-xs text-gray-400 ml-1">/ 목표 {goal.weekly_attendance_days}일</span>
                </span>
              </div>
            )}

            {/* 달성률 바 */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">달성률</span>
                <span className={`text-sm font-bold ${
                  (goal.achievement_rate || 0) >= 80 ? 'text-green-600' :
                  (goal.achievement_rate || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {goal.achievement_rate !== null ? `${goal.achievement_rate}%` : '0%'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    (goal.achievement_rate || 0) >= 80 ? 'bg-green-500' :
                    (goal.achievement_rate || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(goal.achievement_rate || 0, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {goal.remaining_days > 0 ? `남은 기간: ${goal.remaining_days}일` : '기간 종료'}
              </p>
            </div>
          </div>

          {/* 메모 */}
          {goal.notes && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500">{goal.notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }


  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 헤더 */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <AcademicCapIcon className="h-8 w-8 mr-3 text-blue-600" />
          학습 목표 관리
        </h1>
        <p className="text-gray-500 mt-1">
          학생의 학습 목표를 설정하고 진행 상황을 관리합니다
        </p>
      </div>

      {/* 학생 선택 */}
      <div className="mb-6">
        {selectedStudent ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">{selectedStudent.name.charAt(0)}</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{selectedStudent.name}</div>
                  <div className="text-xs text-gray-500">{selectedStudent.email}</div>
                </div>
              </div>
              <button
                onClick={() => { setSelectedStudentId(null); setSelectedStudent(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                다른 학생 선택
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="학생 이름으로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing && searchTerm.trim()) {
                      // Enter 시 즉시 서버 검색 후 선택
                      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
                      try {
                        const res = await fetch(
                          `${API_BASE_URL}/v1/owner/members?search=${encodeURIComponent(searchTerm.trim())}&limit=5`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (res.ok) {
                          const data = await res.json();
                          const members = data.members || [];
                          if (members.length > 0) {
                            const exact = members.find((m: any) => m.name === searchTerm.trim());
                            const s = exact || members[0];
                            setSelectedStudentId(s.id);
                            setSelectedStudent(s);
                            setSearchTerm('');
                          }
                        }
                      } catch {}
                    }
                  }}
                  className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
                {searchLoading ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                ) : searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" type="button">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
                {searchTerm && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {students.length === 0 ? (
                      <div className="px-4 py-3 text-gray-500 text-center text-sm">검색 결과가 없습니다</div>
                    ) : (
                      students.map(student => (
                        <button
                          key={student.id}
                          onClick={() => { setSelectedStudentId(student.id); setSelectedStudent(student); setSearchTerm(''); }}
                          className="w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900 text-sm">{student.name}</div>
                          <div className="text-xs text-gray-500">{student.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <span className="text-sm text-gray-500 whitespace-nowrap">
                {searchTerm ? `${students.length}명` : `총 ${totalStudents}명`}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 학습 목표 섹션 */}
          <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              학습 목표
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              학생과 학부모가 설정한 목표를 확인하고 관리합니다
            </p>
          </div>
          {/* 통합된 목표 추가 버튼 */}
          {selectedStudentId && (
            <button
              onClick={() => {
                setFormData({
                  goal_setter: 'student',
                  goal_period: 'weekly',
                  start_date: getNextMonday(),
                  end_date: getSunday(getNextMonday()),
                  daily_study_hours: 4,
                  weekly_attendance_days: 5,
                  target_subjects: {},
                  notes: '',
                  auto_renew: true,
                });
                setIsCreateModalOpen(true);
              }}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              목표 추가
            </button>
          )}
        </div>

        {/* 목표 카드들 - 1컬럼 레이아웃 */}
        {selectedStudentId && activeGoals ? (
          <div className="space-y-4">
            {/* 학생 목표 */}
            {activeGoals.student_goals.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">학생 목표</h3>
                {activeGoals.student_goals.map((goal) => renderGoalCard(goal, 'border-blue-500'))}
              </>
            )}

            {/* 학부모 목표 */}
            {activeGoals.parent_goals.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mt-6">학부모 목표</h3>
                {activeGoals.parent_goals.map((goal) => renderGoalCard(goal, 'border-purple-500'))}
              </>
            )}

            {activeGoals.student_goals.length === 0 && activeGoals.parent_goals.length === 0 && (
              <p className="text-gray-500 text-center py-4">설정된 목표가 없습니다</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <ChartBarIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">학생을 선택하면 목표를 확인할 수 있습니다</p>
          </div>
        )}
      </div>

      {/* 목표 생성 모달 */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {formData.goal_setter === 'student' ? '학생' : '학부모'} 목표 생성
              </h2>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateGoal} className="p-6 space-y-6">
              {/* 목표 설정자 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  목표 설정자 *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, goal_setter: 'student' }))}
                    className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.goal_setter === 'student'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg font-semibold">👨‍🎓 학생 목표</div>
                      <div className="text-xs mt-1">학생이 직접 설정</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, goal_setter: 'parent' }))}
                    className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.goal_setter === 'parent'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg font-semibold">👨‍👩‍👧 학부모 목표</div>
                      <div className="text-xs mt-1">학부모가 설정</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* 주간 목표 기간 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  목표 주간 (월~일) *
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => {
                      // 선택한 날짜의 월요일로 맞춤
                      const d = new Date(e.target.value);
                      const day = d.getDay();
                      const diff = day === 0 ? -6 : 1 - day;
                      d.setDate(d.getDate() + diff);
                      const monday = formatDateKST(d);
                      setFormData((prev) => ({ ...prev, start_date: monday }));
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <span className="text-gray-500">~</span>
                  <input
                    type="date"
                    value={formData.end_date}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">날짜를 선택하면 해당 주의 월요일~일요일로 자동 설정됩니다</p>
              </div>

              {/* 자동 갱신 */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.auto_renew}
                    onChange={(e) => setFormData((prev) => ({ ...prev, auto_renew: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <div>
                  <span className="text-sm font-medium text-gray-700">매주 자동 갱신</span>
                  <p className="text-xs text-gray-500">주간 목표가 끝나면 동일한 목표로 다음 주 자동 생성</p>
                </div>
              </div>

              {/* 일일 목표 시간 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  일일 목표 시간 (시간)
                </label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={formData.daily_study_hours}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      daily_study_hours: parseFloat(e.target.value),
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* 주간 출석 목표 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  주간 출석 목표 (일)
                </label>
                <input
                  type="number"
                  min="0"
                  max="7"
                  value={formData.weekly_attendance_days}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      weekly_attendance_days: parseInt(e.target.value),
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* 메모 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  메모 (선택사항)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="목표에 대한 추가 설명..."
                />
              </div>

              {/* 버튼 */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 목표 기록 섹션 */}
      {selectedStudentId && (
        <div className="space-y-6">
          {/* 필터 섹션 */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">필터</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 기간 필터 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  기간
                </label>
                <select
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1month">최근 1개월</option>
                  <option value="3months">최근 3개월</option>
                  <option value="6months">최근 6개월</option>
                  <option value="1year">최근 1년</option>
                  <option value="all">전체</option>
                </select>
              </div>

              {/* 설정자 필터 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  설정자
                </label>
                <select
                  value={filterSetter}
                  onChange={(e) => setFilterSetter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">전체</option>
                  <option value="student">학생</option>
                  <option value="parent">학부모</option>
                </select>
              </div>

              {/* 달성 여부 필터 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  달성 여부
                </label>
                <select
                  value={filterAchieved}
                  onChange={(e) => setFilterAchieved(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">전체</option>
                  <option value="achieved">달성</option>
                  <option value="not_achieved">미달성</option>
                </select>
              </div>
            </div>
          </div>

          {/* 목표 기록 테이블 */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {isLoadingHistory ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">목표 기록을 불러오는 중...</p>
              </div>
            ) : historyGoals.length === 0 ? (
              <div className="p-12 text-center">
                <ClockIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">목표 기록이 없습니다</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        기간
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        설정자
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        목표 유형
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        달성률
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        달성 여부
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {historyGoals
                      .filter((goal) => {
                        // 달성 여부 필터링 (프론트엔드)
                        if (filterAchieved === 'all') return true;
                        const achieved = goal.is_achieved || (goal.achievement_rate != null && goal.achievement_rate >= 80);
                        if (filterAchieved === 'achieved') return achieved;
                        if (filterAchieved === 'not_achieved') return !achieved;
                        return true;
                      })
                      .map((goal) => (
                      <tr
                        key={goal.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {goal.start_date} ~ {goal.end_date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {goal.goal_setter === 'student' ? '학생' : '학부모'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {goal.goal_period === 'weekly' ? '주간' : '월간'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {goal.achievement_rate && goal.achievement_rate > 0 ? (
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${goal.achievement_rate}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-700">
                                {goal.achievement_rate}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(goal.is_achieved || (goal.achievement_rate && goal.achievement_rate >= 80)) ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              달성
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              미달성
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                    }
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default GoalManagementPage;
